import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { Row, Col } from 'react-bootstrap'
import {
  getOptionsConfig,
  getContainerOptions,
  getDefaultOptionsValues,
  getEnabledOptions,
  getTypeName,
} from '@rawgraphs/rawgraphs-core'
import ChartOptionNumber from './ChartOptionTypes/ChartOptionNumber'
import ChartOptionText from './ChartOptionTypes/ChartOptionText'
import ChartOptionColor from './ChartOptionTypes/ChartOptionColor'
import ChartOptionColorScaleWrapper from './ChartOptionTypes/ChartOptionColorScaleWrapper'
import ChartOptionBoolean from './ChartOptionTypes/ChartOptionBoolean'
import get from 'lodash/get'
import map from 'lodash/map'
import styles from './ChartOptions.module.scss'
import omit from 'lodash/omit'
import { CHART_IDS } from '../../charts/chartIds'

const CHART_OPTION_COMPONENTS = {
  number: ChartOptionNumber,
  text: ChartOptionText,
  color: ChartOptionColor,
  colorScale: ChartOptionColorScaleWrapper,
  boolean: ChartOptionBoolean,
}

function ensureBlendModeOption(config) {
  if (!config?.linksBlendMode?.options) {
    return config
  }

  const nextOptions = Array.isArray(config.linksBlendMode.options)
    ? [...config.linksBlendMode.options]
    : []

  if (!nextOptions.includes('color-burn')) {
    nextOptions.push('color-burn')
  }

  return {
    ...config,
    linksBlendMode: {
      ...config.linksBlendMode,
      options: nextOptions,
    },
  }
}

function getPartialMapping(mapping, dimension, repeatIndex) {
  const nv = get(mapping[dimension], `value[${repeatIndex}]`)
  return {
    ...mapping,
    [dimension]: {
      ...mapping[dimension],
      value: [nv],
    },
  }
}

function getPartialMappedData(mappedData, dimension, repeatIndex) {
  return Array.isArray(mappedData)
    ? mappedData.map((datum) => {
        const value = get(datum[dimension], `[${repeatIndex}]`)
        return {
          ...datum,
          [dimension]: value,
        }
      })
    : mappedData
}

function getDefaultForRepeat(def, index) {
  if (Array.isArray(def.repeatDefault)) {
    return get(def.repeatDefault, `[${index}]`, def.default)
  }
  return def.default
}

function isAxisGroupName(groupName) {
  return ['axis', 'axes'].includes(String(groupName || '').toLowerCase())
}

function WrapControlComponent({
  type,
  optionId,
  setVisualOptions,
  label,
  repeatIndex,
  ...props
}) {
  const Component = CHART_OPTION_COMPONENTS[type]

  const remainingOptions = useMemo(() => {
    if (type !== 'colorScale') {
      return null
    }

    return Object.keys(omit(props.visualOptions, optionId))
      .map((k) => JSON.stringify(get(props.visualOptions, k, '')))
      .join('-')
  }, [type, props.visualOptions, optionId])

  const domainFromChart = useMemo(() => {
    if (type !== 'colorScale') {
      return null
    }
    if (props.domain && props.chart[props.domain]) {
      //as sometimes the current chart is not in synch with current options (chart is set before options, we just handle an exception)
      //everything should be ok on the next render.
      try {
        const domain = props.chart[props.domain](
          props.mappedData,
          props.mapping,
          props.visualOptions
        )
        return domain
      } catch (e) {
        return null
      }
    } else {
      return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    props.chart,
    props.domain,
    props.mappedData,
    props.mapping,
    remainingOptions,
  ])

  const mappingValue = useMemo(() => {
    if (type !== 'colorScale') {
      return null
    }
    return domainFromChart
      ? '__custom__'
      : get(props.mapping, `[${props.dimension}].value`)
  }, [domainFromChart, props.dimension, props.mapping, type])

  const colorDataType = useMemo(() => {
    if (type !== 'colorScale') {
      return null
    }
    if (domainFromChart) {
      return domainFromChart.type
    }
    return props.dataTypes[mappingValue]
      ? getTypeName(props.dataTypes[mappingValue])
      : 'string'
  }, [type, props.dataTypes, domainFromChart, mappingValue])

  const colorDataset = useMemo(() => {
    if (type !== 'colorScale') {
      return null
    }
    if (domainFromChart) {
      return domainFromChart.domain
    }

    if (props.mappedData) {
      return props.mappedData
        .map((d) => get(d, props.dimension))
        .filter(
          (item) => item !== undefined && !(Array.isArray(item) && !item.length)
        )
    } else {
      return []
    }
  }, [type, props.dimension, domainFromChart, props.mappedData])

  const handleControlChange = useCallback(
    (nextValue) => {
      setVisualOptions((visualOptions) => {
        let newValue = nextValue
        if (repeatIndex !== undefined) {
          newValue = visualOptions[optionId] || []
          newValue[repeatIndex] = nextValue
        }
        return {
          ...visualOptions,
          [optionId]: newValue,
        }
      })
    },
    [optionId, repeatIndex, setVisualOptions]
  )

  return (
    <Component
      type={type}
      domainFromChart={domainFromChart}
      mappingValue={mappingValue}
      colorDataType={colorDataType}
      colorDataset={colorDataset}
      optionId={optionId}
      label={
        repeatIndex !== undefined ? (
          <React.Fragment>
            {label} ({repeatIndex + 1})
          </React.Fragment>
        ) : (
          label
        )
      }
      {...omit(props, [
        'mapping',
        'visualOptions',
        'chart',
        'dataset',
        'dataTypes',
        'mappedData',
      ])}
      onChange={handleControlChange}
    />
  )
}

const ChartOptions = ({
  chart,
  dataset,
  mapping,
  dataTypes,
  visualOptions,
  setVisualOptions,
  error,
  mappedData,
  groupFilter,
  className,
  hideGroupChrome = false,
  modernGroupChrome = false,
  emptyState,
  chartGroupContent = null,
  axisGroupContent = null,
}) => {
  const chartId = chart?.metadata?.id || ''
  const hasSeriesMapping = Boolean(get(mapping, 'series.value.length', 0))

  const optionsConfig = useMemo(() => {
    let config = getOptionsConfig(chart?.visualOptions)

    if (chartId === CHART_IDS.BUMP_CHART && config.sortSeriesBy) {
      config = {
        ...config,
        sortSeriesBy: {
          ...config.sortSeriesBy,
          label: 'Sort panels by',
        },
      }
    }

    if (
      chartId === CHART_IDS.ALLUVIAL_DIAGRAM ||
      chartId === CHART_IDS.SANKEY_DIAGRAM
    ) {
      config = ensureBlendModeOption(config)
    }

    return config
  }, [chart, chartId])

  const [collapseStatus, setCollapseStatus] = useState({})

  const enabledOptions = useMemo(() => {
    return getEnabledOptions(optionsConfig, visualOptions, mapping)
  }, [optionsConfig, visualOptions, mapping])

  const effectiveEnabledOptions = useMemo(() => {
    if (chartId !== CHART_IDS.BUMP_CHART) {
      return enabledOptions
    }

    return {
      ...enabledOptions,
      sortSeriesBy: hasSeriesMapping && enabledOptions.sortSeriesBy,
    }
  }, [chartId, enabledOptions, hasSeriesMapping])

  // const enabledGroupsByName = useMemo(() => {
  //   const enabledGroupsNames = Object.keys(optionsConfig).map(optionName => enabledOptions[optionName] ? optionsConfig[optionName].group : null).filter(x => !!x)
  //   return mapValues(keyBy(enabledGroupsNames), x => true)
  // }, [enabledOptions, optionsConfig])
  // // #TODO we can use enabledGroupsByName to disable the group

  const optionsDefinitionsByGroup = useMemo(() => {
    return Object.keys(optionsConfig).reduce((acc, optionId) => {
      const option = optionsConfig[optionId]
      const group = option?.group || ''
      if (!acc[group]) {
        acc[group] = {}
      }
      acc[group][optionId] = option
      return acc
    }, {})
  }, [optionsConfig])

  useEffect(() => {
    const groups = {}
    for (const option in optionsConfig) {
      const group = optionsConfig[option].group
      if (!groups.hasOwnProperty(group)) {
        const normalizedGroup = (group || '').toLowerCase()
        groups[group] =
          normalizedGroup === 'artboard' || normalizedGroup === 'labels'
            ? false
            : true
      }
    }
    setCollapseStatus(groups)
  }, [optionsConfig])

  const visibleOptionGroups = useMemo(() => {
    const entries = Object.entries(optionsDefinitionsByGroup)

    if (!groupFilter) {
      return entries
    }

    const predicate = Array.isArray(groupFilter)
      ? (groupName) => groupFilter.includes(groupName)
      : groupFilter

      return entries
      .filter(([groupName]) => predicate(groupName))
      .map(([groupName, options]) => [
        groupName,
        Object.fromEntries(
          Object.entries(options).filter(([optionId]) => {
            if (optionId === 'showLabelsOutline' || optionId === 'showGrid') {
              return false
            }
            if (String(groupName).toLowerCase() === 'artboard' && optionId === 'background') {
              return false
            }
            return true
          })
        ),
      ])
      .filter(([, options]) => Object.keys(options).length > 0)
  }, [groupFilter, optionsDefinitionsByGroup])

  const visibleOptionGroupsWithAxisContent = useMemo(() => {
    if (!axisGroupContent) {
      return visibleOptionGroups
    }

    const hasAxisGroup = visibleOptionGroups.some(([groupName]) =>
      isAxisGroupName(groupName)
    )

    if (hasAxisGroup) {
      return visibleOptionGroups
    }

    return [...visibleOptionGroups, ['axis', {}]]
  }, [axisGroupContent, visibleOptionGroups])

  const containerOptions = useMemo(() => {
    const defaultOptionsValues = getDefaultOptionsValues(optionsConfig, mapping)
    const opts = {
      ...defaultOptionsValues,
      ...visualOptions,
    }
    return getContainerOptions(optionsConfig, opts)
  }, [mapping, optionsConfig, visualOptions])

  const getGroupDisplayName = useCallback((groupName) => {
    if (!groupName) return ''

    const normalized = String(groupName).toLowerCase()
    if (isAxisGroupName(normalized)) {
      return 'Axes'
    }
    if (normalized === 'colors') {
      return 'Nodes/Links'
    }
    if (normalized === 'labels') {
      return 'Label display'
    }

    return String(groupName)
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  }, [])

  return (
    <div className={[styles['chart-options'], className || 'col-4 col-xl-3'].join(' ')}>
      {visibleOptionGroupsWithAxisContent.length === 0 && emptyState ? (
        <div className="text-muted small">{emptyState}</div>
      ) : null}
      {map(visibleOptionGroupsWithAxisContent, ([groupName, options]) => {
        const normalizedGroupName = String(groupName).toLowerCase()
        const canCollapseGroup = String(groupName).toLowerCase() !== 'labels'
        const shouldCollapseGroup =
          !hideGroupChrome && canCollapseGroup && collapseStatus[groupName]
        return (
          <div
            key={groupName}
            groupname={groupName}
            className={[
              styles['options-group'],
              hideGroupChrome ? styles['group-chrome-hidden'] : '',
              modernGroupChrome ? styles['modern-group-chrome'] : '',
              normalizedGroupName === 'labels' ? styles['labels-group-flat'] : '',
              shouldCollapseGroup ? styles['collapsed'] : '',
            ].join(' ')}
          >
            {!hideGroupChrome && (
              <Row className="sticky-top">
                <Col
                  className={`d-flex justify-content-between align-items-center ${styles['group-header']}`}
                >
                  <span className="accordion-section__title">
                    {getGroupDisplayName(groupName)}
                  </span>
                  {canCollapseGroup && (
                    <button
                      type="button"
                      aria-label={
                        collapseStatus[groupName]
                          ? `Expand ${getGroupDisplayName(groupName)}`
                          : `Collapse ${getGroupDisplayName(groupName)}`
                      }
                      className={[styles['collapse-button'], 'cursor-pointer'].join(
                        ' '
                      )}
                      onClick={() =>
                        setCollapseStatus({
                          ...collapseStatus,
                          [groupName]: !collapseStatus[groupName],
                        })
                      }
                    ></button>
                  )}
                </Col>
                </Row>
            )}
            {normalizedGroupName === 'chart' && chartGroupContent ? chartGroupContent : null}
            {isAxisGroupName(normalizedGroupName) && axisGroupContent
              ? axisGroupContent
              : null}
            {map(options, (def, optionId) => {
              // repeated options: notice that value is set to a default if undefined
              // this is caused by changes in shapes of the mapping object
              // (when a new value is dragged to the dimension that repeats the option)
              // the same approach is applied in option validation by the raw core lib
              return def.repeatFor ? (
                get(
                  mapping,
                  `[${def.repeatFor}].value`,
                  []
                ).map((v, repeatIndex) => (
                  <WrapControlComponent
                    className={styles['chart-option']}
                    key={optionId + repeatIndex}
                    repeatIndex={repeatIndex}
                    {...def}
                    optionId={optionId}
                    error={error?.errors?.[optionId + repeatIndex]}
                    value={
                      visualOptions?.[optionId]?.[repeatIndex] ??
                      getDefaultForRepeat(def, repeatIndex)
                    }
                    mapping={
                      def.type === 'colorScale'
                        ? getPartialMapping(mapping, def.repeatFor, repeatIndex)
                        : undefined
                    }
                    chart={def.type === 'colorScale' ? chart : undefined}
                    dataset={def.type === 'colorScale' ? dataset : undefined}
                    dataTypes={
                      def.type === 'colorScale' ? dataTypes : undefined
                    }
                    visualOptions={
                      def.type === 'colorScale' ? visualOptions : undefined
                    }
                    mappedData={getPartialMappedData(
                      mappedData,
                      def.repeatFor,
                      repeatIndex
                    )}
                    setVisualOptions={setVisualOptions}
                    isEnabled={effectiveEnabledOptions[optionId]}
                  />
                ))
              ) : (
                <WrapControlComponent
                  className={styles['chart-option']}
                  key={optionId}
                  {...def}
                  optionId={optionId}
                  error={error?.errors?.[optionId]}
                  value={visualOptions?.[optionId]}
                  mapping={def.type === 'colorScale' ? mapping : undefined}
                  chart={def.type === 'colorScale' ? chart : undefined}
                  dataset={def.type === 'colorScale' ? dataset : undefined}
                  dataTypes={def.type === 'colorScale' ? dataTypes : undefined}
                  visualOptions={
                    def.type === 'colorScale' ? visualOptions : undefined
                  }
                  mappedData={mappedData}
                  setVisualOptions={setVisualOptions}
                  isEnabled={effectiveEnabledOptions[optionId]}
                />
              )
            })}
            {groupName === 'artboard' && visualOptions.showLegend && (
              <p className="small">
                The final output will be {containerOptions?.width}px *{' '}
                {containerOptions?.height}px including the legend.
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ChartOptions
